/**
 * br-reforma-case-testimonial — donor reveal, stat count and video control.
 *
 * Reforma action lists a-111–a-114 move each element from y:50px and
 * opacity:0 to rest over 600ms with 100/200/300/400ms delays. The donor
 * triggers each at a 10% viewport offset. Hidden states are JS-armed only,
 * so no-JS remains readable. Reduced motion skips reveals/counting and
 * pauses autoplay on the poster.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setup = (section) => {
    if (section.dataset.brReformaCaseTestimonialReady === 'true') return;
    section.dataset.brReformaCaseTestimonialReady = 'true';

    const counters = [...section.querySelectorAll('[data-br-case-count]')];
    let hasCounted = false;

    const runCounters = () => {
      if (hasCounted || reducedMotion.matches) return;
      hasCounted = true;

      counters.forEach((counter, index) => {
        const target = Number(counter.dataset.brCaseCount);
        if (!Number.isFinite(target)) return;

        const delay = index === 0 ? 300 : 600;
        const duration = 1000;
        counter.textContent = '0';

        window.setTimeout(() => {
          const start = performance.now();

          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            counter.textContent = String(Math.round(target * progress));
            if (progress < 1) requestAnimationFrame(tick);
          };

          requestAnimationFrame(tick);
        }, delay);
      });
    };

    if (!reducedMotion.matches && 'IntersectionObserver' in window) {
      section.classList.add('br-reforma-case-testimonial--armed');

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            section.classList.remove('br-reforma-case-testimonial--armed');
            runCounters();
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px -10% 0px' }
      );

      observer.observe(section);
    } else {
      runCounters();
    }

    const video = section.querySelector('.br-reforma-case-testimonial__video');
    const toggle = section.querySelector('[data-br-case-toggle]');
    if (!(video instanceof HTMLVideoElement) || !toggle) return;

    const pauseIcon = toggle.querySelector('[data-br-case-pause]');
    const playIcon = toggle.querySelector('[data-br-case-play]');
    const labels = {
      play: toggle.dataset.labelPlay,
      pause: toggle.dataset.labelPause,
    };
    let userPaused = false;

    const syncControl = () => {
      const paused = video.paused;
      pauseIcon.hidden = paused;
      playIcon.hidden = !paused;
      toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
      toggle.setAttribute('aria-label', paused ? labels.play : labels.pause);
    };

    const syncPlayback = () => {
      if (reducedMotion.matches || userPaused) {
        video.pause();
      } else {
        video.play().catch(syncControl);
      }
      syncControl();
    };

    toggle.addEventListener('click', () => {
      if (video.paused) {
        userPaused = false;
        video.play().catch(syncControl);
      } else {
        userPaused = true;
        video.pause();
      }
    });

    video.addEventListener('play', syncControl);
    video.addEventListener('pause', syncControl);
    reducedMotion.addEventListener('change', syncPlayback);
    // Sources are deferred (data-src) and attach on approach; syncPlayback
    // starts playback once they do. Until then the poster holds.
    window.brVideoDefer?.attach(video, { onAttached: syncPlayback });
    syncPlayback();
  };

  document.querySelectorAll('[data-br-reforma-case-testimonial]').forEach(setup);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-br-reforma-case-testimonial]').forEach(setup);
  });
})();
